import { useState } from 'react';
import { ApiError, paymentMethodsApi, type PaymentMethodView, type PaymentMethodsView } from '../api/client';
import { useData } from '../lib/useData';
import { Button, Card, Chip, Dialog, LoadError, Loading, Notice } from './ui';

/**
 * Billing → Payment methods (1.13.0, Teampace 3092): the company's cards and bank accounts, kept by
 * Ceexo's payment processor (Teampace's card plugin), never by Stitch. Add one on the processor's own
 * page, choose the default, remove one, and turn autopay on or off. A card that expires within 30
 * days says so here (and its billing contacts get an email).
 */
export function PaymentMethodsCard({ domain }: { domain: string }) {
  const data = useData(() => paymentMethodsApi.list(domain), [domain], { live: false });
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const [removing, setRemoving] = useState<PaymentMethodView | null>(null);
  const [view, setView] = useState<PaymentMethodsView | null>(null);
  const v = view ?? data.data;
  const added = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment') === 'added';

  async function run(fn: () => Promise<PaymentMethodsView>, ok: string) {
    setBusy(true);
    setSaid(null);
    try {
      setView(await fn());
      setSaid({ tone: 'ok', text: ok });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setBusy(false);
    }
  }

  const add = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const { url } = await paymentMethodsApi.page(domain);
      window.location.assign(url);
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof ApiError ? err.message : 'The card page couldn’t be opened. Try again.' });
      setBusy(false);
    }
  };

  if (data.error && !v) {
    return (
      <Card title="Payment methods">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  }
  if (!v) {
    return (
      <Card title="Payment methods">
        <Loading label="Loading payment methods" />
      </Card>
    );
  }
  const expiring = v.methods.find((m) => m.default && (m.expiring || m.expired));
  return (
    <Card title="Payment methods">
      {!v.available ? (
        <p className="muted">{v.reason}</p>
      ) : (
        <>
          {added && <Notice tone="ok">Added. It’s listed below (it can take a moment to show).</Notice>}
          {expiring && (
            <Notice tone={expiring.expired ? 'crit' : 'warn'} title={expiring.expired ? 'Your default card has expired' : 'Your default card expires soon'}>
              {expiring.label} {expiring.expired ? 'has expired' : `expires ${expiring.expires}`}. Add a new card and make it the default.
            </Notice>
          )}
          {v.methods.length === 0 ? (
            <p className="muted">No card or bank account yet.</p>
          ) : (
            <ul className="payment-methods">
              {v.methods.map((m) => (
                <li key={m.id}>
                  <div className="grow">
                    <strong>{m.label}</strong> {m.default && <Chip tone="brand" small>Default</Chip>}
                    <div className="muted small">
                      {m.kind === 'ach' ? 'Bank account' : m.expires ? `Expires ${m.expires}` : 'Card'}
                      {m.expired ? ' · expired' : m.expiring ? ' · expires soon' : ''}
                    </div>
                  </div>
                  <div className="row-actions">
                    {!m.default && (
                      <Button size="small" variant="ghost" disabled={busy} onClick={() => void run(() => paymentMethodsApi.makeDefault(domain, m.id), `${m.label} is the default now.`)}>
                        Make default
                      </Button>
                    )}
                    <Button size="small" variant="ghost" disabled={busy} onClick={() => setRemoving(m)}>
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="billing-actions">
            <Button variant="secondary" icon="plus" disabled={busy} onClick={() => void add()}>
              Add a card or bank account
            </Button>
          </div>
          <label className="check">
            <input type="checkbox" checked={v.autopay} disabled={busy || v.net30} onChange={(e) => void run(() => paymentMethodsApi.autopay(domain, e.target.checked), e.target.checked ? 'Autopay is on.' : 'Autopay is off.')} /> Pay invoices automatically with the default card (autopay)
          </label>
          <p className="muted small">
            {v.net30 ? 'Your company pays by invoice (Net 30), so autopay doesn’t apply.' : 'Card numbers are kept by Ceexo’s payment processor, never by Stitch. The same cards can pay for your other Ceexo services.'}
          </p>
        </>
      )}
      {said && (
        <Notice tone={said.tone} title={said.tone === 'ok' ? 'Done' : 'That didn’t work'}>
          {said.text}
        </Notice>
      )}
      {removing && (
        <Dialog
          open
          onClose={() => setRemoving(null)}
          title={`Remove ${removing.label}?`}
          foot={
            <>
              <Button variant="ghost" onClick={() => setRemoving(null)}>
                Keep it
              </Button>
              <Button
                variant="danger"
                disabled={busy}
                onClick={() => {
                  const m = removing;
                  setRemoving(null);
                  void run(() => paymentMethodsApi.remove(domain, m.id), `Removed ${m.label}.`);
                }}
              >
                Remove
              </Button>
            </>
          }
        >
          <p>{removing.default ? 'It’s your default. Another card becomes the default, or, with none left, autopay stops until you add one.' : 'It won’t be used for payments any more.'}</p>
        </Dialog>
      )}
    </Card>
  );
}
